// Typed client for the Parley API.
//
// Both this app and the Flutter client speak the same /api/v1 surface, so the
// types here mirror the Go DTOs exactly. Keep them in sync when the Go side
// changes; a mismatch shows up as a silently missing field rather than an error.

import { authHeaders } from './auth'

export type PartKind =
  | 'text'
  | 'reasoning'
  | 'tool_call'
  | 'tool_result'
  | 'file'
  | 'source'
  | 'error'
  | 'task'

export type PartStatus = 'streaming' | 'done' | 'error'

export interface Part {
  index: number
  kind: PartKind
  /** Tool name, filename or source title. Separate from `text`, which for a
   *  tool call holds the streaming arguments. */
  name?: string
  text?: string
  data?: unknown
  tool_call_id?: string
  status: PartStatus
}

export type MessageRole = 'user' | 'agent' | 'system'

export interface Message {
  id: string
  conversation_id: string
  seq: number
  role: MessageRole
  sender_agent_id?: string
  run_id?: string
  reply_to_id?: string
  status: string
  starred: boolean
  created_at: number
  parts: Part[]
  attachments?: Attachment[]
}

// Attachment is a binary — a voice note's recording, or a reply's
// synthesized speech — riding alongside a message. Playback goes through
// api.attachmentAudioUrl(id), not a URL handed back directly: the id is a
// reference the client turns into a request, the same as every other id in
// this file.
export interface Attachment {
  id: string
  mime: string
  filename?: string
  size: number
  duration_ms?: number
  transcript?: string
  created_at: number
}

export interface SearchResult {
  message_id: string
  conversation_id: string
  conversation_title: string
  conversation_kind: string
  role: string
  /** The match highlighted with ‹...› markers — see wire.SearchResult. */
  snippet: string
  created_at: number
}

export type ConversationKind = 'direct' | 'group' | 'broadcast'

export interface Conversation {
  id: string
  kind: ConversationKind
  title: string
  primary_agent_id?: string
  member_agent_ids?: string[]
  last_message_at?: number
  last_message_preview?: string
  unread_count: number
  pinned: boolean
  muted: boolean
  archived: boolean
  draft?: string
  // Group-only settings; see internal/storage/migrations/*/0005_room_autonomy.sql
  // for what each does. Always false for a direct or broadcast conversation.
  auto_route_mentions: boolean
  sniff_mode: boolean
  // The room's brief — topic, rules, what it is for — handed to every member
  // on every turn. Group-only, same as the two above; see
  // internal/storage/migrations/*/0007_group_instructions.sql.
  instructions?: string
}

// SILENCE_SENTINEL must stay byte-identical to chat.silenceSentinel on the
// server. A sniff-mode agent replies with exactly this when it has nothing
// to add, and a message whose only content is this string is not rendered.
export const SILENCE_SENTINEL = '[[no reply]]'

export interface Agent {
  id: string
  connection_id: string
  remote_agent_id: string
  name: string
  description?: string
  avatar_url?: string
  model_id?: string
  provider?: string
  tools?: AgentTool[]
  pinned: boolean
  muted: boolean
  archived: boolean
  online: boolean
  last_seen_at?: number
}

export interface AgentTool {
  id: string
  name: string
  description?: string
}

export interface ProbeResult {
  base_url: string
  protocol: string
  server_name?: string
  discovery?: string
  agents: {
    id: string
    name: string
    description?: string
    model_id?: string
    provider?: string
    tools?: AgentTool[]
  }[]
}

export interface Approval {
  id: string
  run_id: string
  conversation_id: string
  tool_call_id: string
  tool_name: string
  args?: unknown
  state: 'pending' | 'approved' | 'denied'
}

export interface Me {
  id: string
  email: string
  display_name: string
  avatar_url?: string
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId?: string,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Every request carries a freshly minted Clerk session token. The cookie
  // would work for the browser alone, but the header is the one code path the
  // Flutter client shares, and testing the path both clients use is worth more
  // than saving a function call.
  const res = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
      ...(init?.headers ?? {}),
    },
  })

  if (!res.ok) {
    // Every endpoint returns the same error envelope, so one code path handles
    // all failures — and the request id is what makes a user report traceable.
    let code = 'unknown'
    let message = res.statusText
    let requestId: string | undefined
    try {
      const body = await res.json()
      code = body?.error?.code ?? code
      message = body?.error?.message ?? message
      requestId = body?.error?.request_id
    } catch {
      // A non-JSON error body (a proxy error page) leaves the defaults.
    }
    throw new ApiError(res.status, code, message, requestId)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  me: () => request<Me>('/me'),

  listAgents: () => request<Agent[]>('/agents'),
  getAgent: (id: string) => request<Agent>(`/agents/${id}`),
  updateAgent: (id: string, patch: Partial<Agent>) =>
    request<Agent>(`/agents/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteAgent: (id: string) => request<void>(`/agents/${id}`, { method: 'DELETE' }),

  probe: (url: string, auth?: { kind: string; token?: string; header_name?: string }) =>
    request<ProbeResult>('/directory/probe', {
      method: 'POST',
      body: JSON.stringify({ url, auth }),
    }),
  addAgents: (body: {
    base_url: string
    auth?: { kind: string; token?: string; header_name?: string }
    agent_ids: string[]
  }) => request<Agent[]>('/directory/add', { method: 'POST', body: JSON.stringify(body) }),
  listConversations: () => request<Conversation[]>('/conversations'),
  getConversation: (id: string) => request<Conversation>(`/conversations/${id}`),
  createConversation: (body: {
    kind: ConversationKind
    agent_ids: string[]
    title?: string
  }) => request<Conversation>('/conversations', { method: 'POST', body: JSON.stringify(body) }),
  updateConversation: (id: string, patch: Partial<Conversation>) =>
    request<Conversation>(`/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteConversation: (id: string) =>
    request<void>(`/conversations/${id}`, { method: 'DELETE' }),
  markRead: (id: string) => request<void>(`/conversations/${id}/read`, { method: 'POST' }),

  listMessages: (conversationId: string, afterSeq = 0, limit = 100) =>
    request<Message[]>(
      `/conversations/${conversationId}/messages?after_seq=${afterSeq}&limit=${limit}`,
    ),
  // Runs is a list, not a single agent message: a direct chat wakes exactly
  // one agent, a group wakes whoever was @mentioned (possibly nobody — the
  // message still posts), and a broadcast wakes every member.
  sendMessage: (conversationId: string, body: { text: string; reply_to_id?: string }) =>
    request<{ message: Message; runs: { agent_message: Message; run_id: string }[] }>(
      `/conversations/${conversationId}/messages`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  stopRun: (runId: string) => request<void>(`/runs/${runId}/stop`, { method: 'POST' }),

  // Not built on request(): a multipart body needs the browser to set its own
  // Content-Type boundary, which request()'s fixed 'application/json' header
  // would stomp on.
  uploadVoice: async (conversationId: string, audio: Blob, durationMs?: number) => {
    const form = new FormData()
    form.append('audio', audio, 'voice-note')
    if (durationMs) form.append('duration_ms', String(Math.round(durationMs)))

    const res = await fetch(`/api/v1/conversations/${conversationId}/voice`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...(await authHeaders()) },
      body: form,
    })
    if (!res.ok) {
      let code = 'unknown'
      let message = res.statusText
      let requestId: string | undefined
      try {
        const body = await res.json()
        code = body?.error?.code ?? code
        message = body?.error?.message ?? message
        requestId = body?.error?.request_id
      } catch {
        // A non-JSON error body (a proxy error page) leaves the defaults.
      }
      throw new ApiError(res.status, code, message, requestId)
    }
    return res.json() as Promise<{ message: Message; runs: { agent_message: Message; run_id: string }[] }>
  },

  speakMessage: (messageId: string) => request<Attachment>(`/messages/${messageId}/speak`, { method: 'POST' }),

  // A plain relative URL, not a request() call: an <audio> element fetches
  // this itself and cannot attach the Authorization header request() adds —
  // it authenticates through the __session cookie Clerk's SDK already sets
  // on this origin, the same fallback auth.Middleware gives the WebSocket.
  attachmentAudioUrl: (attachmentId: string) => `/api/v1/attachments/${attachmentId}/audio`,

  addMember: (conversationId: string, agentId: string, role?: string) =>
    request<Conversation>(`/conversations/${conversationId}/members`, {
      method: 'POST',
      body: JSON.stringify({ agent_id: agentId, role }),
    }),
  removeMember: (conversationId: string, agentId: string) =>
    request<void>(`/conversations/${conversationId}/members/${agentId}`, { method: 'DELETE' }),

  decideApproval: (id: string, approved: boolean, reason?: string) =>
    request<void>(`/approvals/${id}`, {
      method: 'POST',
      body: JSON.stringify({ approved, reason }),
    }),

  probeAgent: (id: string) => request<Agent>(`/agents/${id}/probe`, { method: 'POST' }),
  listApprovals: (runId: string) => request<Approval[]>(`/runs/${runId}/approvals?pending=true`),
  // conversationId scopes the meter to one contact ("what has talking to
  // this agent cost today"); omitted, it is the account-wide total.
  usage: (conversationId?: string) =>
    request<{ since: number; runs: number; cost: number; conversation_id?: string }>(
      conversationId ? `/usage?conversation_id=${conversationId}` : '/usage',
    ),

  search: (q: string, limit = 30) =>
    request<SearchResult[]>(`/search?q=${encodeURIComponent(q)}&limit=${limit}`),

  starMessage: (id: string, starred: boolean) =>
    request<void>(`/messages/${id}/star`, { method: 'POST', body: JSON.stringify({ starred }) }),
  listStarred: () => request<Message[]>('/messages/starred'),

  regenerateMessage: (id: string) =>
    request<{ agent_message: Message; run_id: string }>(`/messages/${id}/regenerate`, { method: 'POST' }),

  forwardMessage: (id: string, conversationId: string) =>
    request<{ message: Message; runs: { agent_message: Message; run_id: string }[] }>(
      `/messages/${id}/forward`,
      { method: 'POST', body: JSON.stringify({ conversation_id: conversationId }) },
    ),

  // A plain relative URL: the browser's own download handling (right click,
  // or the anchor's `download` attribute) is what actually fetches it,
  // authenticated the same way attachmentAudioUrl is.
  exportUrl: (conversationId: string, format: 'markdown' | 'json' = 'markdown') =>
    `/api/v1/conversations/${conversationId}/export?format=${format === 'json' ? 'json' : 'markdown'}`,

  vapidPublicKey: () => request<{ public_key: string }>('/push/vapid-key'),
  subscribePush: (sub: PushSubscriptionJSON, label?: string) =>
    request<void>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint: sub.endpoint, keys: sub.keys, label }),
    }),
  unsubscribePush: (endpoint: string) =>
    request<void>('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
}
